import { ApisauceInstance } from 'apisauce'
import {
  UserResponse, 
  ChannelMemberResponse, 
  ChannelResponse, 
  Channel, 
  ReactionAPIResponse, 
  LocalMessage,
  Reaction,
  Attachment,
  ChannelFilters,
  ChannelSort,
  ChannelOptions,
  StreamChat
 } from 'stream-chat'
import { MessageSimpleProps} from 'stream-chat-react-native'
import { CodeleapQueryClient } from '@codeleap/query'

export enum ChatFileTypes {
  Audio = 'audio',
  File = 'file',
  Giphy = 'giphy',
  Image = 'image',
  Imgur = 'imgur',
  Video = 'video',
  VoiceRecording = 'voiceRecording',
}

export type ChatListUsersFilters = {
  offset?: number
  limit?: number
  name?: string
  include_me?: boolean
  exclude_chat?: ChatChannel['id']
}

export type ChatChannel = ChannelResponse & {
  id: string
  member_count: number
  type: string
  team: string
  name: string
  created_by: {
    id: string
  }
}

export type ChatChannelInstance = Channel

export type InsertChatUser = Pick<ChatUser, 'id' | 'name'>

export type ChatGroupBody = Pick<ChatChannel , 'name'> & {
  members: InsertChatUser[]
  image?: string
}

export type ChatUser =  UserResponse & {
  id: string
  name?: string
  tenant_id?: string
  access_token?: string
  job_title?: string
}

export type ChatMember = ChannelMemberResponse 

export type ChatPhoto = {
  id: number,
  created_datetime: string,
  file: string,
  filename: string | null
}

export type ChatMessage = MessageSimpleProps

export type FocusedMessageInfo = {
  data: ChatMessage
  measures: { pageX: number, pageY: number, height: number, width: number, x: number, y: number  } 
  noFooter?: boolean
}

export type ChatSelectModes = 'forward' | 'delete' 

export type MessageReaction = Reaction

export type MessageReactionResponse = ReactionAPIResponse

export type ChatLocalMesage = LocalMessage & {
  pollName?: string
  customMessage?: string
}

export type ThreadParent = ChatLocalMesage

export type HandleReactionProps = {
  message: ChatLocalMesage
  channel: Channel
  onReaction?: (emoji: string) => void
  onReactionSent?: (reaction: MessageReactionResponse) => void
  emoji: string
}

export type SimpleChannel = {
  id: string
  type: string
}

export type TogglePinMessageProps = {
  message: ChatLocalMesage
  channel: SimpleChannel
}

export type SendSystemMessageProps = {
  channel: SimpleChannel
  text: string
  target_id?: ChatUser['id']
  target_name?: ChatUser['name']
  targets?: Array<{ id: ChatUser['id'], name: ChatUser['name'] }>
  action?: 'members_added' | 'moderators_granted'
  count?: number
}

export type AddMembersProps = ChatServiceCallbacks & {
  channelId: ChatChannel['id']
  selectedMembers: InsertChatUser[]
}

export type RemoveMemberProps = ChatServiceCallbacks & {
  channelId: ChatChannel['id']
  member: InsertChatUser
}

export type MakeModeratorProps = ChatServiceCallbacks & {
  channelId: ChatChannel['id']
  member: ChatMember
}

export type ChatServiceCallbacks<T = any> = {
  onSuccess?: (response: T) => void
  onError?: (error: Error) => void
  onFinally?: () => void
}

export type GiphyAttachment = Attachment & {
  custom: {
    giphy_id: string
    aspect_ratio: number
  }
}

export type MessageGrouping = {
  lastGroupMessage: boolean
  firstGroupMessage: boolean
}

export type ForwardMessagesProps = ChatServiceCallbacks & {
  channelsToForward: SimpleChannel[]
  messages: ChatLocalMesage[]
  messageText?: string
  onProgress?: (current: number, total: number) => void
  onOpenChannel?: (channel: SimpleChannel) => void
}

export type ListChatFilter = {
  filters?: ChannelFilters
  sort?: ChannelSort
  options?: ChannelOptions
}

export const MessageStatusTypes = {
  FAILED: 'failed',
  RECEIVED: 'received',
  SENDING: 'sending',
};


export type UpdatePushPreferencesBody = {
  chatId: string | null;
  chatLevel: 'all' | 'mentions' | 'none';
  disabledUntil?: string
}

export type GetChatClientOptions = { connectUser?: boolean }

export type ChatPackageConstructorOptions = {
  api: ApisauceInstance,
  queryClient: CodeleapQueryClient
  getChatClient: (options?: GetChatClientOptions) => Promise<StreamChat> 
}
